import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/i18n/assets";

export function Timeline() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const lineScale = useScroll({ target: ref, offset: ["start center", "end center"] }).scrollYProgress;

  return (
    <section
      id="lineage"
      data-testid="timeline-section"
      ref={ref}
      className="relative overflow-hidden bg-clan-surface py-28 lg:py-40"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        {/* Sticky parallax image column */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Overline>{t.timeline.overline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl">
                {t.timeline.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 overflow-hidden rounded-lg border border-clan-ink/10">
                <motion.div style={{ y: imgY }} className="will-change-transform">
                  <img
                    src={IMAGES.castleRuin}
                    alt="Ancient castle ruins"
                    className="aspect-[3/4] w-full scale-110 object-cover"
                  />
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Timeline events */}
        <div className="relative lg:col-span-7">
          <div className="absolute left-[7px] top-2 h-full w-px bg-clan-ink/[0.06] lg:left-[9px]">
            <motion.div style={{ scaleY: lineScale }} className="h-full w-px origin-top bg-clan-accent" />
          </div>

          <div className="flex flex-col gap-14 pl-10 lg:pl-14">
            {t.timeline.events.map((e, i) => (
              <Reveal key={e.year} delay={i * 0.05} y={30}>
                <div data-testid={`timeline-event-${e.year}`} className="relative">
                  <span className="absolute -left-10 top-1.5 grid h-4 w-4 place-items-center rounded-full border border-clan-accent bg-clan-bg lg:-left-14">
                    <span className="h-1.5 w-1.5 rounded-full bg-clan-accent" />
                  </span>
                  <span className="font-mono text-sm font-medium tracking-widest text-clan-accent">{e.year}</span>
                  <h3 className="mt-2 font-serif text-2xl font-medium text-clan-ink lg:text-3xl">{e.title}</h3>
                  <p className="mt-3 max-w-lg text-base leading-relaxed text-clan-muted">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
